import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, X, Euro } from 'lucide-react';
import { Button } from './ui/Button';
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-342" data-mp="30m2bekw216sl">
      <div className="flex items-center justify-between" data-id="element-343" data-mp="30mvb51wliw18">
        <label className="text-sm font-medium text-slate-600" data-id="element-344" data-mp="2mm12ci5myt21">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-345" data-mp="2rlnqhmn05tpm" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-346" data-mp="3jki0ny0z65ry" />
    </div>;
}
type InfoKey = 'master' | 'localization' | 'insights' | null;
interface InfoPopoverProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  title: string;
  body: string;
}
function InfoPopover({
  isOpen,
  onToggle,
  onClose,
  title,
  body
}: InfoPopoverProps) {
  return <span className="relative inline-flex" data-id="element-347" data-mp="guhr0ew2xyt7">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-label={title} className="w-5 h-5 rounded-full bg-[#6C2BD9]/[0.08] hover:bg-[#6C2BD9]/[0.16] border border-[#6C2BD9]/30 text-[#6C2BD9] flex items-center justify-center transition-colors" data-id="element-348" data-mp="ls9kvegabv01">
        <Info className="w-3 h-3" data-id="element-349" data-mp="226sz7w5knqwk" />
      </button>
      <AnimatePresence data-id="element-350" data-mp="225zo6izkyf8m">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15
      }} className="absolute z-30 top-full left-0 mt-2 w-[280px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-351" data-mp="267up3mdlmqlv">
            <div className="flex items-start justify-between gap-3" data-id="element-352" data-mp="3302um3vxfd8h">
              <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-353" data-mp="332xcbnnk2jy7">
                {title}
              </p>
              <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-354" data-mp="2u0o79db23is7">
                <X className="w-3.5 h-3.5" data-id="element-355" data-mp="s92puxgvo7lh" />
              </button>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-356" data-mp="3l42t94zteoqe">
              {body}
            </p>
          </motion.div>}
      </AnimatePresence>
    </span>;
}
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const [products, setProducts] = useState(10);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(4);
  const [countries, setCountries] = useState(10);
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);
  const [costBenchmarkOpen, setCostBenchmarkOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState<InfoKey>(null);
  // List-price unit economics
  const masterSnakCostBase = 2900;
  const masterSnakCostFloor = 1900;
  const campaignDuration = 3;
  const localizationCostBase = 999;
  const monthlyInsightsFeeBase = 99;
  const durationRebate = 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  // --- Volume discounts ---
  const COUNTRY_DISCOUNT_FLOOR = 2;
  const COUNTRY_DISCOUNT_CEILING = 50;
  const COUNTRY_DISCOUNT_MAX = 0.5;
  const countryDiscount = Math.min(COUNTRY_DISCOUNT_MAX, Math.max(0, (countries - COUNTRY_DISCOUNT_FLOOR) / (COUNTRY_DISCOUNT_CEILING - COUNTRY_DISCOUNT_FLOOR) * COUNTRY_DISCOUNT_MAX));
  const effectiveCountryCost = localizationCostBase * (1 - countryDiscount);
  const countryDeploymentCost = effectiveCountryCost;
  const totalAnnualCampaigns = products * campaignsPerProduct;
  const MASTER_DISCOUNT_FLOOR = 1;
  const MASTER_DISCOUNT_CEILING = 100;
  const masterProgress = Math.min(1, Math.max(0, (totalAnnualCampaigns - MASTER_DISCOUNT_FLOOR) / (MASTER_DISCOUNT_CEILING - MASTER_DISCOUNT_FLOOR)));
  const effectiveMasterSnakCost = masterSnakCostBase - (masterSnakCostBase - masterSnakCostFloor) * masterProgress;
  const masterSnakCost = effectiveMasterSnakCost;
  const INSIGHTS_DISCOUNT_FLOOR = 2;
  const INSIGHTS_DISCOUNT_CEILING = 50;
  const INSIGHTS_DISCOUNT_MAX = 0.5;
  const insightsDiscount = Math.min(INSIGHTS_DISCOUNT_MAX, Math.max(0, (countries - INSIGHTS_DISCOUNT_FLOOR) / (INSIGHTS_DISCOUNT_CEILING - INSIGHTS_DISCOUNT_FLOOR) * INSIGHTS_DISCOUNT_MAX));
  const effectiveMonthlyInsightsFee = monthlyInsightsFeeBase * (1 - insightsDiscount);
  const monthlyInsightsFee = effectiveMonthlyInsightsFee;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-357" data-mp="26a8snrj26rbd">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-358" data-mp="2b8twe0xirt4k">
        {/* Header */}
        <div className="mb-6" data-id="element-359" data-mp="1wjxl7v061zzq">
          <div className="flex items-center gap-3 mb-1" data-id="element-360" data-mp="1wiuz00dsglq5">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-361" data-mp="21hz6mml2ebed">
              Snakker
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-362" data-mp="2p7a15mbtn9k2">
            Fast Marketing<sup className="text-[0.6em] font-medium" data-id="element-363" data-mp="2u0cv3vkigl0s">™</sup>{' '}
            Revenue Calculator
          </h1>
          <p className="text-base text-slate-500 mt-1" data-id="element-364" data-mp="ihykxyhmrjap">
            How Snakker monetizes a single pharma customer
          </p>
          <p className="text-xs text-slate-400 mt-0.5" data-id="element-365" data-mp="s9mgaahb5khg">
            This is the same calculator we use with customers — fixed unit
            economics scaled across brands, campaigns and countries. Adjust the
            sliders to see annual revenue per account at different portfolio
            sizes.
          </p>

          {/* Industry benchmark + Cost benchmark popovers */}
          <div className="mt-3 flex flex-wrap items-start gap-2" data-id="element-366" data-mp="3qtcikl3ij6v7">
            <div className="relative inline-block" data-id="element-367" data-mp="8mepfd6zammh">
              <button type="button" onClick={() => {
              setBenchmarkOpen(v => !v);
              setCostBenchmarkOpen(false);
            }} aria-expanded={benchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-368" data-mp="4k9y4l406230">
                <Info className="w-3.5 h-3.5" data-id="element-369" data-mp="ebuuf8vdyu4h" />
                Industry benchmark
              </button>
              <AnimatePresence data-id="element-370" data-mp="9gozjkk0a549">
                {benchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[380px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-371" data-mp="4jfd8drwy9kn">
                    <div className="flex items-start justify-between gap-3" data-id="element-372" data-mp="1sget1kf0h8mi">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-373" data-mp="1x7wlfk2yy0vv">
                        Big Pharma at a glance
                      </p>
                      <button type="button" onClick={() => setBenchmarkOpen(false)} aria-label="Close benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-374" data-mp="14jqxypkj5xsc">
                        <X className="w-3.5 h-3.5" data-id="element-375" data-mp="1ija5bg5ip53l" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-376" data-mp="2pxmxlnl1ispc">
                      Big pharma typically runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-377" data-mp="2txdum915u52b">
                        20–60 brands
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-378" data-mp="pvdqq8tej6am">
                        100–135 countries
                      </span>{' '}
                      ×{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-379" data-mp="yviu87vh5kji">
                        4–6 campaigns/year
                      </span>
                      .
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="relative inline-block" data-id="element-380" data-mp="72jidxqaacfd">
              <button type="button" onClick={() => {
              setCostBenchmarkOpen(v => !v);
              setBenchmarkOpen(false);
            }} aria-expanded={costBenchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-381" data-mp="kwsx4s8nnkv5">
                <Euro className="w-3.5 h-3.5" data-id="element-382" data-mp="23uyzu7kdouxd" />
                Cost benchmark
              </button>
              <AnimatePresence data-id="element-383" data-mp="1q3ke3sz36fmm">
                {costBenchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[420px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-384" data-mp="1v254l0ojdkxv">
                    <div className="flex items-start justify-between gap-3" data-id="element-385" data-mp="1hp7n4pr0hpys">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-386" data-mp="1cutpegovn6qy">
                        Cost Benchmark — Digital Pharma Campaigns
                      </p>
                      <button type="button" onClick={() => setCostBenchmarkOpen(false)} aria-label="Close cost benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-387" data-mp="13x9k8wnw8qb2">
                        <X className="w-3.5 h-3.5" data-id="element-388" data-mp="1rjg3yg6ojuh4" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-389" data-mp="1moaghcxmq7qx">
                      A typical digital campaign for a legacy brand in one
                      country runs{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-390" data-mp="b940ac4nz5ca">
                        €15,000 – €19,000
                      </span>
                      .
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-391" data-mp="62gpu9knye7p">
                      Compare against Snakker's cost per localized deployment.
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-392" data-mp="jvofhvp6ozmr">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-393" data-mp="ourlbiczn9vs">
            <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-5 space-y-4 border-2 border-[#6C2BD9]/20" data-id="element-394" data-mp="tr8cklfhi4d3">
              <div data-id="element-395" data-mp="yr4emf4mflq0">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-396" data-mp="ypjm0v1vacus">
                  Unit Economics
                </h3>
                <p className="text-[11px] text-[#6C2BD9]/60 mt-0.5" data-id="element-397" data-mp="30i7qt8u5t8co">
                  Fixed Snakker pricing — everything below scales from these
                  unit costs.
                </p>
              </div>

              {/* Master Snak */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-398" data-mp="2njqh2bwo56fr">
                <div className="flex items-center justify-between" data-id="element-399" data-mp="32fzc119j0xmd">
                  <div className="flex items-center gap-2" data-id="element-400" data-mp="2xgmk423st0cx">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-401" data-mp="2xfbctsb59ah2">
                      Master Snak
                    </p>
                    <InfoPopover isOpen={infoOpen === 'master'} onToggle={() => setInfoOpen(infoOpen === 'master' ? null : 'master')} onClose={() => setInfoOpen(null)} title="What is a Master Snak?" body="We start by creating a Master Snak presentation — the original parent version of the campaign. All localized versions are derived from this single source of truth." data-id="element-402" data-mp="36hawksdjmsnp" />
                  </div>
                  <div className="text-right" data-id="element-403" data-mp="cozkdasv49sj">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-404" data-mp="hnszt8rpmavc">
                      €{Math.round(effectiveMasterSnakCost).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-405" data-mp="8l9yit3ko6b7">
                  Per campaign master creation
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-406" data-mp="3q19qkoa1k7os">
                  Volume benefit: drops from €2,900 to €1,900 as total campaigns
                  / year scales from 1 to 100+.
                </p>
              </div>

              {/* Cost per country localization */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-407" data-mp="3l1ndceu7qrfz">
                <div className="flex items-center justify-between" data-id="element-408" data-mp="376bk6bvph916">
                  <div className="flex items-center gap-2" data-id="element-409" data-mp="3c20arppqzflc">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-410" data-mp="3rl2zjx47qe36">
                      Cost per country localization
                    </p>
                    <InfoPopover isOpen={infoOpen === 'localization'} onToggle={() => setInfoOpen(infoOpen === 'localization' ? null : 'localization')} onClose={() => setInfoOpen(null)} title="What is Localization?" body="Localization adapts the Master version to local usage, language and regulatory requirements — so the campaign is market-ready in every country it ships to." data-id="element-411" data-mp="3mmrppmdx43ky" />
                  </div>
                  <div className="text-right" data-id="element-412" data-mp="2ut95am4yvjfg">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-413" data-mp="2puxvucil3axr">
                      €{Math.round(effectiveCountryCost)}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-414" data-mp="10e2eeq1edba9">
                  One-time set-up per country
                </p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-415" data-mp="15frp24kpjmxt">
                  Volume benefit: discount slides from 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>

              {/* Data insights & hosting */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-416" data-mp="hq73ddx66bku">
                <div className="flex items-center justify-between" data-id="element-417" data-mp="mnytq3i61pmx">
                  <div className="flex items-center gap-2" data-id="element-418" data-mp="qoshmwvlz4ft">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-419" data-mp="qn7nh8d7hnih">
                      Data insights & hosting
                    </p>
                    <InfoPopover isOpen={infoOpen === 'insights'} onToggle={() => setInfoOpen(infoOpen === 'insights' ? null : 'insights')} onClose={() => setInfoOpen(null)} title="What are Data Insights?" body="Based on individual HCP engagement, we bi-monthly deliver not only real-world data but also actionable suggestions for improvement — so every campaign keeps getting sharper." data-id="element-420" data-mp="3v27h8x5tzq88" />
                  </div>
                  <div className="text-right" data-id="element-421" data-mp="2ntplwgbnqg77">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-422" data-mp="2nur6pcih6h24">
                      €{effectiveMonthlyInsightsFee.toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-423" data-mp="2wtcov6k1bu2d">
                        / month
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-424" data-mp="243vzyivmivaw">Per country</p>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-425" data-mp="293icsr7ji9j6">
                  Volume benefit: discount slides from 0% at 2 countries up to
                  50% at 50+ countries.
                </p>
              </div>
            </div>

            {/* Portfolio Scale */}
            <div className="bg-slate-50 rounded-2xl p-5 space-y-4 border border-slate-200" data-id="element-426" data-mp="1rm1qcq7v8cxe">
              <div data-id="element-427" data-mp="1nkq50ww0ydv0">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest" data-id="element-428" data-mp="1nlhsfkfgx5a7">
                  Portfolio Scale
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-429" data-mp="1inq0wriqjl6j">
                  Adjust your portfolio size to see how cost scales.
                </p>
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={40} data-id="element-430" data-mp="18wlo9ygj8cev" />
              <SliderInput label="Campaigns per Year" value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={2} max={6} data-id="element-431" data-mp="zum5b0lyiy9a" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={2} max={75} data-id="element-432" data-mp="lat6guuxtnv1" />
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-433" data-mp="g9voojduavu2">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-434" data-mp="z7y72fbvtuiw">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-435" data-mp="1mxiv3cmw7hz1">
                Cost per Localized Campaign
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-436" data-mp="3cexv8i1k0a1m">
                {formatEuro(calc.costPerDeployment)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-437" data-mp="37fbfa0uccfp4">
                per localized deployment
              </p>
            </div>

            {/* Volume + total annual */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-438" data-mp="3h6pcu32qf5fm">
              <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest mb-3" data-id="element-439" data-mp="2tfa863nhgc17">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-440" data-mp="2ojblqllpt43f">
                <div data-id="element-441" data-mp="32ht8oh3sqcka">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-442" data-mp="32i1j82cg55sb">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-443" data-mp="hqiwk6pe8tq1">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-444" data-mp="cqwjbx9kfdh8">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-445" data-mp="ctiq3uomtoye">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-446" data-mp="1d05yp0l0wbh2">
                    localized deployments / year
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-baseline justify-between" data-id="element-447" data-mp="1myviiywhc5zy">
                <p className="text-xs text-slate-500" data-id="element-448" data-mp="1r0qio02ocdc5">Estimated annual cost</p>
                <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-449" data-mp="1qymh3os8zqf1">
                  {formatEuro(calc.totalAnnualCost)}
                  <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-450" data-mp="20pqljukarsvm">
                    / year
                  </span>
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200" data-id="element-451" data-mp="2aphfnv4y45v6">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3" data-id="element-452" data-mp="2epjja9u8r9d3">
                Cost Breakdown
              </p>
              <div className="space-y-2" data-id="element-453" data-mp="340rodovxfee">
                <div className="flex justify-between items-center" data-id="element-454" data-mp="3v5z1x05mqq22">
                  <span className="text-sm text-slate-600" data-id="element-455" data-mp="3v8v08ple3gr1">
                    Avg. cost per localized campaign
                  </span>
                  <span className="text-sm font-semibold text-slate-900" data-id="element-456" data-mp="3l943eh9cwpns">
                    {formatEuro(calc.costPerDeployment)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-1" data-id="element-457" data-mp="hrm08n4j46tp" />
                {calc.firstCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-458" data-mp="d0m1dcbuneok">
                    <span className="text-sm text-slate-500" data-id="element-459" data-mp="cysv3y55ewh3">
                      New masters ({calc.firstCampaigns} ×{' '}
                      {formatEuro(masterSnakCost)})
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-460" data-mp="15izr0on17uck">
                      {formatEuro(calc.firstCampaigns * masterSnakCost)}
                    </span>
                  </div>}
                {calc.followUpCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-461" data-mp="2yyudrwtv3ss1">
                    <span className="text-sm text-slate-500" data-id="element-462" data-mp="2l90m76qz2uem">
                      Follow-up campaigns ({calc.followUpCampaigns} × €1,000)
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-463" data-mp="2q5hco7ln9kuv">
                      {formatEuro(calc.followUpCampaigns * FOLLOW_UP_MASTER_COST)}
                    </span>
                  </div>}
                <div className="flex justify-between items-center" data-id="element-464" data-mp="22ay3f9p0ptp9">
                  <span className="text-sm text-slate-500" data-id="element-465" data-mp="2787oye9adl7t">
                    Localization & set-up
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-466" data-mp="jb5eo6pkyja">
                    {formatEuro(calc.annualDeploymentCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-467" data-mp="fl1k1ntgw3nl">
                  <span className="text-sm text-slate-500" data-id="element-468" data-mp="fj6zwbq0sfkk">
                    Hosting & data insights ({campaignDuration}mo)
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-469" data-mp="odv6c57c03ay">
                    {formatEuro(calc.annualInsightsCost)}
                  </span>
                </div>
              </div>
            </div>

            {/* Strategic callout */}
            <div className="bg-[#6C2BD9]/5 rounded-2xl p-4 border border-[#6C2BD9]/10" data-id="element-470" data-mp="y9y6x6mjbtvs">
              <p className="text-sm font-medium text-[#6C2BD9] leading-relaxed" data-id="element-471" data-mp="tc6g6fxmmdt6">
                Not software pricing — this is annual revenue per account for a
                fully managed campaign operating model. ACV scales predictably
                with each brand, campaign and country added.
              </p>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-472" data-mp="3528ppw2hc9nj">
              Land with a pilot brand in a few countries, expand across the
              portfolio. Same unit economics, compounding ACV.
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-473" data-mp="77kqlvzb721l"></div>
      </div>
    </div>;
}